import { FC, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ContractEntityProvidersFilters, ContractEntityProvidersFiltersRequestModel, FiltersAction, ProviderHubContractEntityProviders, ProviderProfileActionEnum, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { useLocation } from "react-router-dom";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { PaginationResult } from "@app/shared/waffles/exports";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { FlyoutLink } from "@app/shared/Flyouts";
import { NewActionMenu } from "@app/shared/action-menu";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ContractEntityFilters } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityFilters";
import OrganizationProfileActionFlyout from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileActionFlyout";
import { OrganizationProfileOverview } from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileOverview";
import { OrganizationProfileContractEntity } from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileContractEntity";
import { OrganizationProfileKeyPoints } from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileKeyPoints";
import { OrganizationProfileProviders } from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileProviders";
import OrganizationProfileTable from "@app/projects/network-assessment/provider-hub/organization-profile/OrganizationProfileTable";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { NestingParentDropdownItem, DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { ProviderProfileRouteState } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileRouteState";
import { ProviderHubSpecialty } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ProviderHubSpecialtyView";

export const TEMPORARILY_UNAVAILABLE_TEXT = "Temporarily Unavailable";

export const ProviderHubOrganizationProfile: FC = () => {
  const location = useLocation<ProviderProfileRouteState>();
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const userContext = useContext(UserContext);
  const { selectedOrganization } = providerHubContext;
  const organizationActionsEnabled = featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_ORGANIZATION_PROVIDER_ACTIONS);
  const [organizationProviderTableData, setOrganizationProviderTableData] = useState<PaginationResult<ProviderHubContractEntityProviders>>({ items: [], totalCount: 0 });
  const [accessPoints, setAccessPoints] = useState<ServicingAccessPoint[]>([]);
  const providerHubService = useRef(new ProviderHubService()).current;
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [specialtyOptions, setSpecialtyOptions] = useState<DropdownItem<string>[]>([]);
  const [isFilterReset, setIsFilterReset] = useState<boolean>(false);
  const { activeServiceAreaStatusFilters, serviceAreaMatchFilter, serviceAreaOptions, serviceAreaStatusFilters, setActiveServiceAreaStatusFilters, setServiceAreaOptions } = providerHubContext;

  /* oxlint-disable sort-keys */
  const providerNetworkTypeOptions: NestingParentDropdownItem[] = [
    { key: ProviderNetworkTypeEnum.Network, checkboxStatus: CheckboxStateEnum.Unchecked, name: 'In-Network', children: [] },
    {
      key: 'Out-of-Network', name: 'Out-of-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [
        { key: ProviderNetworkTypeEnum.Prospect, name: 'Prospect' },
        { key: ProviderNetworkTypeEnum.Market, name: 'Market' },
        { key: ProviderNetworkTypeEnum.NetNew, name: 'Net New' }
      ]
    }
  ];
  /* oxlint-enable sort-keys */
  const [defaultProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(
    [
      ProviderNetworkTypeEnum.Network, // Add "In-Network"
      ...providerNetworkTypeOptions.flatMap(pnt => pnt.children).map(c => c.key as ProviderNetworkTypeEnum)
    ]
  );

  const outOfNetworkTypes = useRef<ProviderNetworkTypeEnum[]>([
    ProviderNetworkTypeEnum.Prospect,
    ProviderNetworkTypeEnum.Market,
    ProviderNetworkTypeEnum.NetNew,
  ]).current;

  const outOfNetworkAccessPoints = useMemo(
    () => accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)),
    [accessPoints, outOfNetworkTypes]
  );

  const networkAccessPoints = useMemo(
    () => accessPoints.filter(ap => ap.providerNetworkType === ProviderNetworkTypeEnum.Network),
    [accessPoints]
  );

  const accessPointsForExclusion = useMemo(
    () => outOfNetworkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyExcluded),
    [outOfNetworkAccessPoints]
  );

  const accessPointsForTargeting = useMemo(
    () => outOfNetworkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyTargeted),
    [outOfNetworkAccessPoints]
  );

  const accessPointsForTermination = useMemo(
    () => networkAccessPoints.filter(ap => ap.providerActionStatus !== ProviderActionStatusEnum.FullyTerminated),
    [networkAccessPoints]
  );

  const orgIsExcluded = useMemo(
    () => outOfNetworkAccessPoints.length > 0 && outOfNetworkAccessPoints.every(ap => ap.providerActionStatus === ProviderActionStatusEnum.FullyExcluded),
    [outOfNetworkAccessPoints]
  );

  const hasAvailableActions = useMemo(() => {
    const hasExcludeRole = userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER);
    const hasTargetRole = userContext.roles.includes(Roles.PROVIDER_TARGET_USER);
    const hasTerminateRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);
    if (!hasExcludeRole && !hasTargetRole && !hasTerminateRole) return false;
    return (hasExcludeRole && outOfNetworkAccessPoints.length > 0)
      || (hasTargetRole && outOfNetworkAccessPoints.length > 0)
      || (hasTerminateRole && networkAccessPoints.length > 0);
  }, [outOfNetworkAccessPoints, networkAccessPoints, userContext.roles]);

  const fetchAccessPoints = useCallback(() => {
    providerHubService
      .getAccessPointsByOrganization(
        projectContext.project.projectId,
        selectedOrganization?.projectRunConfigurationId,
        selectedOrganization?.npi
      )
      .then(setAccessPoints)
      .catch(() => setAccessPoints([]));
  }, [projectContext.project.projectId, selectedOrganization, providerHubService]);

  useEffect(() => {
    // location.pathname should be /products/:productId/provider-hub[/run/:runConfigId/provider-profile/:providerKey] (where [] is optional)
    // so keeping only the first three ("", "products", productId) and updating the module should be safe regardless of the extra parameters
    const productUrl = location.pathname.split("/").slice(0, 3).join("/") + "/adequacy";
    const middleCrumb: Breadcrumb = {
      onClick: handleGoBack,
      text: "Organization",
    };

    projectContext.setBreadcrumbItems([
      {
        link: productUrl,
        text: projectContext.project.resource.name,
      },
      middleCrumb,
      {
        text: !selectedOrganization
          ? providerHubContext.errorLoadingContractEntity
            ? "Error"
            : "Loading"
          : selectedOrganization?.name,
      }
    ]);
  }, [projectContext.project.resource.name, providerHubContext.selectedOrganization]);

  // Go Back is now browser Back (the URL is the source of truth for which profile shows); this wrapper just
  // clears the organization-profile error state and resets the breadcrumb before popping the history entry.
  const handleGoBack = () => {
    providerHubContext.setErrorLoadingOrganization(false);
    projectContext.setBreadcrumbItems([projectContext.project.resource.name]);
    providerHubContext.goBackFromProfile();
  };

  useEffect(() => {
    if (selectedOrganization) fetchAccessPoints();
  }, [selectedOrganization, fetchAccessPoints]);

  useEffect(() => {
    refreshData();
  }, [pageIndex, pageSize, selectedOrganization]);

  useEffect(() => {
    // The waffles-container is the scrolling element — its height is fixed and table rows scroll within it
    rightColumnRef.current?.querySelector('.waffles-container')?.scrollTo(0, 0);
  }, [pageIndex]);

  useEffect(() => {
    // Check if context already has regionSetIds set (user came from another view)
    if (providerHubContext.filterState.regionSetIds && providerHubContext.filterState.regionSetIds.length > 0) {
      setFilter('regionSetIds', providerHubContext.filterState.regionSetIds);
    }
    if (providerHubContext.filterState.providerNetworkTypes && providerHubContext.filterState.providerNetworkTypes.length > 0) {
      setFilter('providerNetworkTypes', providerHubContext.filterState.providerNetworkTypes);
      setSelectedProviderNetworkTypes(providerHubContext.filterState.providerNetworkTypes);
    }
    if (providerHubContext.filterState.specialtyGroupCodes && providerHubContext.filterState.specialtyGroupCodes.length > 0) {
      setFilter('specialtyGroupCodes', providerHubContext.filterState.specialtyGroupCodes);
    }
  }, []);

  const initialFilterState: ContractEntityProvidersFilters = {
    pageIndex: 0,
    pageSize: 50,
    providerNetworkTypes: providerHubContext.filterState.providerNetworkTypes ?? [],
    regionSetIds: providerHubContext.filterState.regionSetIds ?? [],
    specialtyGroupCodes: providerHubContext.filterState.specialtyGroupCodes ?? [],
  }

  const filtersReducer = (filtersState: ContractEntityProvidersFilters, action: FiltersAction): ContractEntityProvidersFilters => {
    switch (action.type) {
      case 'SET_FILTER':
        return {
          ...filtersState,
          [action.key]: action.value,
        };
      case 'RESET_FILTERS':
        return initialFilterState;
      default:
        return initialFilterState;
    }
  };

  const [filterState, filterStateDispatch] = useReducer(filtersReducer, initialFilterState);
  const [selectedProviderNetworkTypes, setSelectedProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(filterState.providerNetworkTypes.length === 0 ? defaultProviderNetworkTypes : filterState.providerNetworkTypes);

  useEffect(() => {
    if(isFilterReset) {
      refreshData(true);
      setIsFilterReset(false);
    }
  }, [isFilterReset]);

  const refreshData = async (useFirstPage: boolean = false) => {
    const filterRequestModel: ContractEntityProvidersFiltersRequestModel = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      providerNetworkTypes: filterState.providerNetworkTypes,
      regionSetIds: filterState.regionSetIds.map(Number),
      specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
    };

    if (useFirstPage) {
      setPageIndex(1);
      setResetToFirstPage(true);
    }

    if (selectedOrganization) {
      providerHubService.getOrganizationProvidersDetails(useFirstPage ? 1 : pageIndex, pageSize, projectContext?.project?.projectId,
        selectedOrganization.projectRunConfigurationId, selectedOrganization.npi, filterRequestModel)
        .then((data) => {
          setOrganizationProviderTableData(data);
        });
    }
  }

  const setFilter = <T extends keyof ContractEntityProvidersFilters>(key: T, value: ContractEntityProvidersFilters[T]) => {
    filterStateDispatch({ key, type: 'SET_FILTER', value });
  };

  const handleResetAll = async () => {
  filterStateDispatch({ type: 'RESET_FILTERS' });
  setFilter('providerNetworkTypes', []);
  setSelectedProviderNetworkTypes([]);
  setFilter('specialtyGroupCodes', []);
  setFilter('regionSetIds', []);
  setActiveServiceAreaStatusFilters(new Set());

  // Update the ProviderHub context state properly using setFilter
  providerHubContext.setFilter('specialtyGroupCodes', []);
  providerHubContext.setFilter('providerNetworkTypes', []);
  providerHubContext.setFilter('regionSetIds', []);
  setIsFilterReset(true);
}

  const handleSearchClick = async () => {
    await refreshData(true);
  }

  // Guard AFTER all hooks so hook order stays stable across loading/error/loaded renders (Rules of Hooks).
  if (!selectedOrganization && !providerHubContext.errorLoadingOrganization) {
    return <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="loading Organization" />;
  } else if (providerHubContext.errorLoadingOrganization) {
    return <QESAlert type="danger" persist={true}>We did not find the requested Organization.</QESAlert>
  }

  return (
    <>
      <div className="contractentity-profile__grid">
        <div className="contractentity-profile__top-row">
          <div className="contractentity-profile__top-row-buttons">
            <QESButton
              qesType="outline-inherit"
              qesTestId="provider-hub-provider-detail__back-to-results"
              onClick={handleGoBack}
              className="provider-profile__back-to-results"
            >
              Go Back
            </QESButton>
            {(!providerHubContext.isScenarioModelingProcessing &&
              !providerHubContext.isScenarioModelingError &&
              featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
              hasAvailableActions) &&
              <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER, Roles.PROVIDER_TARGET_USER, Roles.TERM_PROVIDER_ADMIN]}>
                <div className="provider-profile__provider-actions-info">
                  <div>
                    <TooltipWrapper text="Actions will affect all applicable provider access points" position="bottom">
                      <QesIcon iconName={QesIcons.InfoCircle} description="Tooltip" />
                    </TooltipWrapper>
                  </div>
                  <NewActionMenu
                    alignRight={true}
                    buttonClassNames="qes-button-primary"
                    buttonText={organizationActionsEnabled
                      ? "Organization Actions"
                      : (
                        <TooltipWrapper text={TEMPORARILY_UNAVAILABLE_TEXT} position="bottom">
                          Organization Actions
                        </TooltipWrapper>
                      )}
                    containerClassnames="provider-profile__provider-actions-menu"
                    disabled={!organizationActionsEnabled}
                    testId="organization-actions-select"
                  >
                    {outOfNetworkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
                        <FlyoutLink
                          content={{
                            body: <OrganizationProfileActionFlyout
                              accessPoints={accessPointsForExclusion.length > 0 ? accessPointsForExclusion : outOfNetworkAccessPoints}
                              onSubmit={fetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Exclude}
                              targetedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForTargeting.length}
                            />,
                            title: "Exclude Organization"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForExclusion.length === 0}
                          size="60"
                          testId="organization-profile__exclude-flyout"
                        >
                          {accessPointsForExclusion.length === 0 ? (
                            <TooltipWrapper text="This organization is fully excluded across all applicable access points" position="bottom">
                              Exclude
                            </TooltipWrapper>
                          ) : (
                            "Exclude"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                    {outOfNetworkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
                        <FlyoutLink
                          content={{
                            body: <OrganizationProfileActionFlyout
                              accessPoints={accessPointsForTargeting.length > 0 ? accessPointsForTargeting : outOfNetworkAccessPoints}
                              excludedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForExclusion.length}
                              onSubmit={fetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Target}
                            />,
                            title: "Target Organization"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForTargeting.length === 0 || orgIsExcluded}
                          size="40"
                          testId="organization-profile__target-flyout"
                        >
                          {accessPointsForTargeting.length === 0 ? (
                            <TooltipWrapper text="This organization is fully targeted across all applicable access points" position="bottom">
                              Target
                            </TooltipWrapper>
                          ) : orgIsExcluded ? (
                            <TooltipWrapper text="This organization is fully excluded across all applicable access points. To target, remove the Exclusion." position="bottom">
                              Target
                            </TooltipWrapper>
                          ) : (
                            "Target"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                    {networkAccessPoints.length > 0 &&
                      <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                        <FlyoutLink
                          content={{
                            body: <OrganizationProfileActionFlyout
                              accessPoints={accessPointsForTermination}
                              onSubmit={fetchAccessPoints}
                              providerAction={ProviderProfileActionEnum.Terminate}
                            />,
                            title: "Terminate Organization"
                          }}
                          classNames="action-menu-item col-12 fw-normal"
                          disabled={accessPointsForTermination.length === 0}
                          size="40"
                          testId="organization-profile__terminate-flyout"
                        >
                          {accessPointsForTermination.length === 0 ? (
                            <TooltipWrapper text="This organization is fully terminated across all applicable access points" position="bottom">
                              Terminate
                            </TooltipWrapper>
                          ) : (
                            "Terminate"
                          )}
                        </FlyoutLink>
                      </RoleRestricted>
                    }
                  </NewActionMenu>
                </div>
              </RoleRestricted>
            }
          </div>
        </div>
        <h1 className="organization-profile__names">
          <div className="organization-profile__name">{selectedOrganization.name}</div>
        </h1>
        <div className="organization-profile__filters-row">
          <ContractEntityFilters
            activeServiceAreaStatusFilters={activeServiceAreaStatusFilters}
            filterProviderHubState={providerHubContext.filterState}
            filterState={filterState}
            handleResetAll={handleResetAll}
            handleSearchClick={handleSearchClick}
            providerNetworkTypeOptions={providerNetworkTypeOptions}
            selectedProviderNetworkTypes={selectedProviderNetworkTypes}
            serviceAreaMatchFilter={serviceAreaMatchFilter}
            serviceAreaOptions={serviceAreaOptions}
            serviceAreaStatusFilters={serviceAreaStatusFilters}
            setActiveServiceAreaStatusFilters={setActiveServiceAreaStatusFilters}
            setFilter={setFilter}
            setProviderHubFilter={providerHubContext.setFilter}
            setSelectedProviderNetworkTypes={setSelectedProviderNetworkTypes}
            setServiceAreaOptions={setServiceAreaOptions}
            setSpecialtyOptions={setSpecialtyOptions}
            specialtyOptions={specialtyOptions}
          />
        </div>

        {/* First Column */}
        <div className="contractentity-profile__grid-column--scroll">
          {featureFlagContext.enabledFeatureFlags.has(Features.AI_VIRTUAL_ASSISTANT) ? (
            <div className="contractentity-profile__cards-row">
              <OrganizationProfileKeyPoints />
            </div>
          ) : (<OrganizationProfileKeyPoints />)}
          <AccordionContainer>
            <OrganizationProfileOverview />
          </AccordionContainer>
          <AccordionContainer>
            <OrganizationProfileContractEntity />
          </AccordionContainer>
          <AccordionContainer>
            <OrganizationProfileProviders />
          </AccordionContainer>
          <AccordionContainer>
            <ProviderHubSpecialty
              specialties={selectedOrganization.specialtyCount}
            />
          </AccordionContainer>
        </div>
        {/* Second Column */}
        <div className="contractentity-profile__grid-column--scroll" ref={rightColumnRef}>
          <OrganizationProfileTable
            organizationProviderTableData={organizationProviderTableData}
            pageIndex={pageIndex}
            pageSize={pageSize}
            resetToFirstPage={resetToFirstPage}
            setOrganizationProviderTableData={setOrganizationProviderTableData}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
            setResetToFirstPage={setResetToFirstPage}
          />
        </div>
      </div>
    </>
  )
}